/**
 * 전과(戰果) 산정.
 *
 * 설계 결정: 랭킹은 보유 전투력이 아니라 한 번의 출정에서 거둔 전과로 매긴다.
 * 그리고 살아서 돌아와야 전과가 확정된다. 그러지 않으면 런 막바지에 병력을
 * 전부 갈아넣어 최대한 죽이는 것이 최적 전략이 되고, 병사 영구 사망의 무게가 사라진다.
 *
 * 점수는 서버가 리플레이를 재생해 다시 계산하므로 여기도 정수 연산만 쓴다.
 */

export interface RunResult {
  /** 처치한 적의 격 총합. */
  readonly killScore: number;
  /** 이번 출정에 데려간 병력 수. */
  readonly deployed: number;
  /** 살아서 돌아온 병력 수. */
  readonly survivors: number;
  /** 장수 생존 여부. 장수가 죽으면 출정 자체가 실패다. */
  readonly leaderAlive: boolean;
  /** 도달한 출정 깊이. */
  readonly depth: number;
  /**
   * 전장 특성의 전과 가산(백분율, `traitMeritPct`). 어려운 특성이 붙은 판을 넘는 것이 이득이 되게 한다.
   * 없으면 0 — 특성이 생기기 전과 같은 값이다.
   */
  readonly bonusPct?: number;
}

/** 전멸해도 남는 최소 배율(백분율). 이게 0이면 마지막 전투가 너무 잔인해진다. */
const FLOOR_PCT = 25;
const RANGE_PCT = 75;

/**
 * 생환 배율(백분율). 데려간 병력 중 살아 돌아온 비율에서 나온다.
 * 많이 죽이되 적게 잃는 쪽이 최고점을 받는다.
 */
export function survivalPct(deployed: number, survivors: number): number {
  if (deployed <= 0) return FLOOR_PCT;
  const s = survivors < 0 ? 0 : survivors > deployed ? deployed : survivors;
  return FLOOR_PCT + Math.floor((RANGE_PCT * s) / deployed);
}

/**
 * 최종 전과. 장수가 쓰러지면 0이다.
 * 깊이 보너스는 따로 곱하지 않는다. 깊은 곳의 적일수록 격이 높아 killScore에 이미 반영된다.
 */
export function meritScore(r: RunResult): number {
  if (!r.leaderAlive) return 0;
  const base = Math.floor((r.killScore * survivalPct(r.deployed, r.survivors)) / 100);
  const bonus = r.bonusPct !== undefined && r.bonusPct > 0 ? Math.floor(r.bonusPct) : 0;
  return bonus > 0 ? Math.floor((base * (100 + bonus)) / 100) : base;
}
